// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useAsyncError from 'Hooks/useAsyncError';
import useNumberUtils from 'Hooks/useNumberUtils';
import useAnalytics from 'Hooks/useAnalytics';
import { API_ACCOUNT_PERSONAL_DATA, API_ACCOUNT_GDPR } from 'Constants';
import AccountPersonalDataComponent from 'Components/Account/AccountPersonalData';

const defaultValues = {
  firstName: '',
  lastName: '',
  operationalConsent: null,
  marketingConsent: null,
  createdOn: '',
};

const AccountPersonalData = memo(({ setErrors }) => {
  const [profile, setProfile] = useState([]);
  const [loading, setLoading] = useState(true);
  const [personalDataLoading, setPersonalDataLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [consentMessages, setConsentMessages] = useState({});
  const [operationalNotification, setOperationalNotification] = useState([]);
  const [marketingNotification, setMarketingNotification] = useState([]);
  const fetcher = useFetch();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const { data: { email, loggedIn } = {} } = useUser();
  const { formatDate } = useNumberUtils();
  const { query, location } = useRouter();
  const analytics = useAnalytics();
  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    shouldFocusError: true,
  });

  const operationalConsentValue = watch('operationalConsent');
  const marketingConsentValue = watch('marketingConsent');
  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });

  const validators = {
    firstName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(100),
    },
    lastName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(100),
    },
  };

  const getAccountPersonalData = () => {
    setLoading(true);

    fetcher(API_ACCOUNT_PERSONAL_DATA).then(res => {
      setProfile({
        userId: res?.account?.userId,
        cookiePolicy: res?.account?.cookiePolicy,
        isTotpEnabled: res?.account?.isTotpEnabled,
        isImpersonated: res?.account?.isImpersonated,
      });
      reset({
        firstName: res?.account?.firstName || '',
        lastName: res?.account?.lastName || '',
        operationalConsent: res?.account?.consent?.operationalConsent || false,
        marketingConsent: res?.account?.consent?.marketingConsent || false,
        createdOn: res?.account?.consent?.createdOn ? formatDate(res?.account?.consent?.createdOn) : '',
      });
      setConsentMessages(res?.blocks);
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  // Set consent notifications
  useEffect(() => {
    if (Object.keys(consentMessages || {}).length) {
      if (operationalConsentValue === false) {
        setOperationalNotification([{ message: consentMessages?.gdpr_form_aggreement_operational_not_checked_hint, type: 'error' }]);
      } else if (operationalConsentValue === true) {
        setOperationalNotification([{ message: consentMessages?.gdpr_form_aggreement_operational_checked_hint, type: 'success' }]);
      } else {
        setOperationalNotification([]);
      }

      if (marketingConsentValue === false) {
        setMarketingNotification([{ message: consentMessages?.gdpr_form_aggreement_marketing_not_checked_hint, type: 'error' }]);
      } else if (marketingConsentValue === true) {
        setMarketingNotification([{ message: consentMessages?.gdpr_form_aggreement_marketing_checked_hint, type: 'success' }]);
      } else {
        setMarketingNotification([]);
      }
    }
  }, [marketingConsentValue, operationalConsentValue, consentMessages]);

  // Get Profile data on page load
  useEffect(() => {
    if (loggedIn) {
      getAccountPersonalData();
    }
  }, [location.pathname, query, email, loggedIn]);

  const handleConsent = (type, value) => {
    setValue(type, value);
    if (type === 'operationalConsent' && value === false) {
      if (marketingConsentValue === true) {
        setValue('marketingConsent', false);
      }
    }
  };

  const handlePersonalData = method => {
    analytics.track(method === 'DELETE' ? 'delete_personal_data' : 'export_personal_data');
    setPersonalDataLoading(method);
    setErrors([]);
    fetcher(API_ACCOUNT_GDPR, { method }).then(() => {
      setErrors([{
        type: 'success',
        message: t('profile.personalDataExported')
      }]);
    })
      .catch(() => {
        setErrors([{
          type: 'success',
          message: t('profile.personalDataExportedError')
        }]);
      })
      .finally(() => {
        setPersonalDataLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  const handleOnSubmit = async () => {
    analytics.track('updated_profile');
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const {
      firstName,
      lastName,
      operationalConsent,
      marketingConsent,
    } = getValues();

    const parsedData = {
      firstName,
      lastName,
      ...profile,
      consent: {
        operationalConsent,
        marketingConsent,
      },
    };

    setSubmitLoading(true);
    fetcher(API_ACCOUNT_PERSONAL_DATA, {
      method: 'PUT',
      body: JSON.stringify(parsedData),
    }).then(() => {
      getAccountPersonalData();
      setErrors([{ type: 'success', message: t('profile.profileSaved') }]);
    }).catch(() => setErrors([{ type: 'error', message: t('profile.profileSavedError') }]))
      .finally(() => {
        setSubmitLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  return (
    <AccountPersonalDataComponent
      loading={ loading }
      validators={ validators }
      register={ register }
      rhfErrors={ rhfErrors }
      operationalNotification={ operationalNotification }
      marketingNotification={ marketingNotification }
      handlePersonalData={ handlePersonalData }
      personalDataLoading={ personalDataLoading }
      handleOnSubmit={ handleOnSubmit }
      submitLoading={ submitLoading }
      operationalConsentValue={ operationalConsentValue }
      handleConsent={ handleConsent }
    />
  );
});

AccountPersonalData.propTypes = {
  setErrors: func,
};

AccountPersonalData.displayName = 'AccountPersonalDataContainer';

export default AccountPersonalData;
